/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from 'app/AppStore';
import { ReactComponent as RenativeLogo } from 'assets/CarBrand/brand_renative_mini.svg';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { SSOLink } from 'components/SSOLink/SSOLink';
import { getElectronicPartsRepairLink } from 'domains/appContext/AppContext.store';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import { getRepairPrice } from 'domains/references';
import { useFetchRepairPrices } from 'domains/references/References.requests';
import { CLARION } from 'domains/ssoLink/SSOLink.types';
import { getCurrency } from 'domains/user';
import { theme, ThemeFontKeyType } from 'styles';
import { Box, Flex, Icon, MarginBox, Pipeline, Text } from 'UI';
import { textFormatter } from 'utils';

const ButtonRepairLink = styled.div`
  display: flex;
  max-width: 400px;
  height: 30px;
  border-radius: 8px;
  margin-top: 5px;
  margin-bottom: 10px;
  border: solid 1px #c7c7c7;
  align-items: center;
  background-color: ${({ theme }) => theme.color.grey_04};
  cursor: pointer;
`;

const ElectronicPartsRepairLink = ({ referenceNumber, type }: { referenceNumber: string; type: ThemeFontKeyType }) => {
  const { t } = useTranslation();
  const repairPrice = useSelector((state: RootState) => getRepairPrice(state, referenceNumber));
  const repairPriceData = repairPrice?.data;
  const currency = useSelector(getCurrency);
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const repairLink = useSelector(getElectronicPartsRepairLink);

  useFetchRepairPrices([referenceNumber], vehicleKey);

  if (isNaN(Number(repairPrice?.data))) {
    return null;
  }

  return (
    <DataContainer
      data={repairPrice?.searchStatus}
      NotFound={() => <></>}
      ErrorState={() => (
        <ErrorWithLabel
          label={t(
            'common.repair_price.backend_error',
            'Repair price temporarily unavailable, please try again later.',
          )}
          narrow
        />
      )}
    >
      {repairLink && repairPriceData && (
        <>
          <Box width={9} />
          <Pipeline size={30} offsetRatio={9} color={theme.color.grey_06} />
          <Flex>
            <ButtonRepairLink data-cy={'reuse-button'}>
              <Icon IconComponent={RenativeLogo} size={50} ml={10} mr={5} display={'block'} noPointer />
              <SSOLink
                ssoType={CLARION}
                renderLinkComponent={() => (
                  <Text type={type} cursor={'pointer'}>
                    {t('catalog.dh.reference_card.action.repair', 'Repair at: {{repairPrice}}', {
                      repairPrice: textFormatter.formatCurrency(repairPriceData, currency),
                    })}
                  </Text>
                )}
                renderLoadingComponent={() => (
                  <Text type={type} cursor={'pointer'}>
                    {t('link.redirecting_in_progress', 'Redirecting...')}
                  </Text>
                )}
              />
              <MarginBox mr={15} />
            </ButtonRepairLink>
          </Flex>
        </>
      )}
    </DataContainer>
  );
};

export default ElectronicPartsRepairLink;
